import { inject, injectable } from '@patsnap/tahiti-core/shared/inversify';
import type { Event } from '@patsnap/tahiti-core/common/event';
import { SpecificationTaskCreateRepository } from '../specification-task-create-repository';
import { SpecificationTaskCreateModel } from '../../models/specification-task-create-model';
import type { SpecificationTaskCreateService } from '../specification-task-create-service';
import type {
    SpecificationJurisdictionOption,
    SpecificationLangDetectResult,
    SpecificationTaskCreatePayload,
    SpecificationTaskCreatePayloadErrors,
    SpecificationTaskCreateState,
    SpecificationTaskCreateSubmitResult,
} from '../../types/specification-task-create';
import {
    SPECIFICATION_CLAIMS_MAX_LENGTH,
    SPECIFICATION_DISCLOSURE_MAX_LENGTH,
} from '../../constants/specification-create';
import { SpecificationJurisdiction, SpecificationStep } from '../../enums';
import _ from 'lodash';
import { HeaderService } from '@telecom/shared/services/header-service/header-service';
import { ConfirmService } from '@/services/confirm-service/confirm-service';
import { I18nService } from '@telecom/shared/services/i18n';
import { SpecificationService } from '../specification-service';
import { TaskNameService } from '@/services/task-name-service/task-name-service';
import { CheckSensitiveWordService } from '@/services/check-sensitive-word/check-sensitive-word';
import wordsCount from 'words-count';
import { CacheRestoreService, SearchCacheKey } from '@/services/cache-restore-service/cache-restore-service';

@injectable()
export class SpecificationTaskCreateServiceImpl implements SpecificationTaskCreateService {
    private _currentTaskId: string | null = null;

    constructor(
        @inject(SpecificationTaskCreateRepository)
        private _repository: SpecificationTaskCreateRepository,
        @inject(SpecificationTaskCreateModel)
        private _model: SpecificationTaskCreateModel,
        @inject(HeaderService)
        private _headerService: HeaderService,
        @inject(ConfirmService)
        private _confirmService: ConfirmService,
        @inject(I18nService)
        private _i18nService: I18nService,
        @inject(SpecificationService)
        private _specificationService: SpecificationService,
        @inject(TaskNameService)
        private _taskNameService: TaskNameService,
        @inject(CheckSensitiveWordService)
        private _checkSensitiveWordService: CheckSensitiveWordService,
        @inject(CacheRestoreService)
        private _cacheRestoreService: CacheRestoreService,
    ) {}

    get onStateChange(): Event<SpecificationTaskCreateState> {
        return this._model.onStatesChange;
    }

    async init(taskId: string): Promise<void> {
        if (this._currentTaskId === taskId) {
            return;
        }

        this._model.update(state => ({
            ...state,
            taskId,
        }));

        const historyInput = await this._repository.getHistoryInput(taskId);
        this._currentTaskId = taskId;
        const { jurisdiction } = this._getJurisdictionConfig();

        // 从缓存中恢复数据（如果历史输入为空）
        const inventionDisclosure = historyInput.inventionDisclosure || this._cacheRestoreService.restore(SearchCacheKey.PATENT_DISCLOSURE);
        const claims = historyInput.claims || this._cacheRestoreService.restore(SearchCacheKey.PATENT_CLAIMS);

        this._model.update(state => ({
            ...state,
            taskId,
            formData: {
                ...state.formData,
                inventionDisclosure,
                claims,
                // 如果历史输入有权利要求，则使用历史输入的受理局，否则使用当前部署环境的受理局
                jurisdiction: historyInput.claims ? historyInput.jurisdiction || jurisdiction : jurisdiction,
            },
        }));
    }

    getState(): SpecificationTaskCreateState {
        return this._model.modelData;
    }

    setFormData(payload: Partial<SpecificationTaskCreatePayload>): void {
        this._model.update(state => ({
            ...state,
            formData: {
                ...state.formData,
                ...payload,
            },
        }));
    }

    async validateFormData(): Promise<SpecificationTaskCreatePayloadErrors> {
        const { formData } = this.getState();
        const errors: SpecificationTaskCreatePayloadErrors = {};

        if (!formData.claims) {
            errors.claims = '';
        } else {
            // 使用 words-count 计算单词数
            const claimsWordCount = wordsCount(formData.claims);
            if (claimsWordCount > SPECIFICATION_CLAIMS_MAX_LENGTH) {
                errors.claims = '';
            }
        }

        if (!formData.inventionDisclosure) {
            errors.inventionDisclosure = '';
        } else {
            // 使用 words-count 计算单词数
            const disclosureWordCount = wordsCount(formData.inventionDisclosure);
            if (disclosureWordCount > SPECIFICATION_DISCLOSURE_MAX_LENGTH) {
                errors.inventionDisclosure = '';
            }
        }

        this._model.update(state => ({
            ...state,
            formErrors: errors,
        }));

        return errors;
    }

    getFormErrors(): SpecificationTaskCreatePayloadErrors {
        return this.getState().formErrors;
    }

    getJurisdictions(): SpecificationJurisdictionOption[] {
        return this._getJurisdictionConfig().jurisdictionOptions;
    }

    async submit(): Promise<SpecificationTaskCreateSubmitResult> {
        try {
            this._setLoadingState(true);

            const validationResult = await this._handleFormValidation();
            if (validationResult) return validationResult;

            const state = this.getState();
            await this._checkSensitiveWordService.checkSensitiveWord(`${state.formData.inventionDisclosure}\n${state.formData.claims}`);
            const langDetectResult = await this._repository.langDetect(state.formData);

            if (!langDetectResult.success) {
                const detectionHandledResult = await this._handleLanguageDetectionResult(langDetectResult);
                if (detectionHandledResult) return detectionHandledResult;
            }

            // 需要获取最新的 state 去创建任务
            const result = await this._createSpecificationTask(this.getState());
            await this._taskNameService.setTaskName(state.taskId!, this._i18nService.t('aiSpecification.untitledSpecification'));
            return result;
        } finally {
            this._setLoadingState(false);
        }
    }

    private _setLoadingState(isLoading: boolean): void {
        this._model.update(state => ({
            ...state,
            loadingSubmit: isLoading,
        }));
    }

    private async _handleFormValidation(): Promise<SpecificationTaskCreateSubmitResult | undefined> {
        const errors = await this.validateFormData();
        return _.isEmpty(errors) ? undefined : {
            success: false,
            formErrors: errors,
        };
    }

    private async _handleLanguageDetectionResult(
        result: SpecificationLangDetectResult,
    ): Promise<SpecificationTaskCreateSubmitResult | undefined> {
        const message = this._buildConfirmationMessage(result);
        const confirmed = await this._showConfirmationDialog(result, message);

        if (!confirmed) return result;

        this._updateJurisdiction(result.recommendJurisdiction!);
        return undefined;
    }

    private _buildConfirmationMessage(result: SpecificationLangDetectResult): string {
        return result.recommendJurisdiction
            ? `${result.message} <span class="font-semibold">${result.recommendJurisdiction}</span>`
            : result.message!;
    }

    private async _showConfirmationDialog(
        result: SpecificationLangDetectResult,
        message: string,
    ): Promise<boolean> {
        return this._confirmService.confirm(message, {
            title: result.messageTitle,
            submitText: this._i18nService.t('aiSpecification.confirmAndStart'),
            cancelText: this._i18nService.t('aiSpecification.backToModify'),
            submitBtnClass: !result.recommendJurisdiction ? 'hidden' : '',
        });
    }

    private _updateJurisdiction(jurisdiction: SpecificationJurisdiction): void {
        this.setFormData({ jurisdiction });
    }

    private async _createSpecificationTask(
        state: SpecificationTaskCreateState,
    ): Promise<SpecificationTaskCreateSubmitResult> {
        await this._repository.createTask(state.taskId!, state.formData);
        this._specificationService.switchStep(SpecificationStep.ConfirmDisclosure);
        return { success: true };
    }

    private _getJurisdictionConfig(): { jurisdiction: SpecificationJurisdiction; jurisdictionOptions: SpecificationJurisdictionOption[] } {
        // 受理局选项
        const jurisdictionOptions: SpecificationJurisdictionOption[] = [
            {
                value: SpecificationJurisdiction.CN,
                label: 'CNIPA',
            },
            {
                value: SpecificationJurisdiction.US,
                label: 'USPTO',
            },
            {
                value: SpecificationJurisdiction.EU,
                label: 'EPO',
            },
            // 后续可以在这里添加其他语言环境对应的优先受理局
        ];
        // 根据语言环境获取对应的优先受理局
        const jurisdictionMap: Record<string, SpecificationJurisdiction> = {
            CN: SpecificationJurisdiction.CN,
            US: SpecificationJurisdiction.US,
            EU: SpecificationJurisdiction.EU,
            // 后续可以在这里添加其他语言环境对应的优先受理局
        };
        const currentLang = this._headerService.account?.siteRegion ?? 'US';
        const jurisdiction = jurisdictionMap[currentLang];

        const priorityIndex = jurisdictionOptions.findIndex(j => j.value === jurisdiction);
        if (priorityIndex > 0) {
            const [priority] = jurisdictionOptions.splice(priorityIndex, 1);
            jurisdictionOptions.unshift(priority);
        }
        return {
            jurisdiction,
            jurisdictionOptions,
        };
    }
}
